import React, { useCallback, useMemo } from 'react';
import {
  ArrowRightOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  MessageOutlined,
  RiseOutlined,
  SearchOutlined,
  StarOutlined,
  SyncOutlined,
  ThunderboltOutlined,
  WarningOutlined
} from '@ant-design/icons';
import styled, { css } from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { useOrbitContext } from '../OrbitContext';
import { TOrbit } from '../i18n/TModules';
import {
  ORBIT_GLOBAL_STARTERS,
  OrbitPromptStarter,
  getContextualStarters,
  isStarterAllowed
} from '../promptStarters';
import { orbitRelativeTime } from '../relativeTime';
import { getOrbitScreenLabelKey } from '../screenLabels';
import { OrbitSectionLabel, orbitTokens, orbitType } from '../styles';
import useOrbitConversations, { ORBIT_ALL_CHATS } from '../useOrbitConversations';
import useOrbitStarters from '../useOrbitStarters';

/** The frame draws three (`4952:9019`, `9027`, `9035`); the feed is the app's whole turn history. */
const RECENT_SESSION_LIMIT = 3;

// Leading glyph of each suggested-action row (`4952:8995`), outline and monochrome like the context
// chips (`OrbitChipIcon.tsx`). The frame's three rows are fixture copy, so the mapping is by starter
// id and falls back for any starter added later.
const STARTER_ICONS: Record<string, typeof RiseOutlined> = {
  'global.changed': SyncOutlined,
  'global.attention': ExclamationCircleOutlined,
  'global.why': SearchOutlined,
  'global.trend': RiseOutlined,
  'global.report': BarChartOutlined,
  'global.next': ArrowRightOutlined,
  'customerInsights.trend': RiseOutlined,
  'customerInsights.attention': WarningOutlined,
  'customerInsights.report': BarChartOutlined,
  'customer360.engagement': RiseOutlined,
  'customer360.risk': WarningOutlined,
  // One per card family (`promptStarters.ts` ORBIT_FAMILY_STARTERS). PLA-9563 section 2a.
  'card.lifecycle': SyncOutlined,
  'card.engagement': ThunderboltOutlined,
  'card.behaviour': RiseOutlined,
  'card.value': DollarOutlined,
  'card.risk': WarningOutlined,
  'card.readiness': CheckCircleOutlined,
  'card.sentiment': MessageOutlined,
  'card.dataQuality': SearchOutlined
};

const StarterIcon: React.FC<{ id: string }> = ({ id }) => {
  const Icon = STARTER_ICONS[id] ?? StarOutlined;

  return <Icon data-testid={`orbit-home-starter-icon-${id}`} />;
};

const Root = styled.div`
  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-top: 8px;
  }
`;

// Only the unrecognised-screen salutation: on a screen Orbit recognises the detected-context
// headline, its body and the chips are one sticky section, `OrbitContextBlock`.
const GreetCard = styled.div`
  min-height: 84px;
  padding: 18px 20px;
  border: 1px solid ${orbitTokens.border};
  border-radius: 12px;
`;

const GreetTitle = styled.h2`
  margin: 0;
  color: ${orbitTokens.text};
  ${orbitType.contextTitle}
  font-weight: 700;
`;

const GreetBody = styled.p`
  margin: 6px 0 0;
  color: ${orbitTokens.textSecondary};
  ${orbitType.contextSubtitle}
  font-weight: 400;
`;

// The frame butts each list straight against its heading; the breathing room below the headings and
// above View all history is a deliberate deviation (user, 2026-09-11).
const ActionsLabel = styled(OrbitSectionLabel)`
  padding-top: 14px;
  margin-bottom: 10px;
`;

const SessionsLabel = styled(OrbitSectionLabel)`
  padding-top: 12px;
  margin-bottom: 10px;
`;

// The first row pads 14 at the top and every later one 12. Each row is wrapped in its own `li`, so
// `:first-of-type` on the button would match all of them; the list addresses the first item instead.
const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;

  > * + * {
    margin-top: 8px;
  }

  > li:first-child > * {
    padding-top: 14px;
  }
`;

const rowRules = css`
  display: flex;
  align-items: flex-start;
  width: 100%;
  margin: 0;
  padding: 12px 14px;
  background: transparent;
  border: 1px solid ${orbitTokens.border};
  border-radius: 10px;
  cursor: pointer;
  font-family: inherit;
  text-align: left;

  /* figma-spec-v3.md section 4c. The row's own ink is set by its title and subtitle spans, so the
     hover variant reaches this rule as the stroke alone. */
  &:hover:not(:disabled),
  &:focus-visible {
    border-color: ${orbitTokens.action};
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

const Row = styled.button`
  ${rowRules}
`;

const IconTile = styled.span`
  color: ${orbitTokens.textSecondary};
  display: inline-flex;
  flex: 0 0 30px;
  align-items: center;
  justify-content: center;
  height: 30px;
  margin-right: 12px;
  background: ${orbitTokens.surface};
  border-radius: 8px;
  font-size: 13px;
  line-height: 13px;
`;

const SessionIcon = styled.span`
  color: ${orbitTokens.textSecondary};
  display: inline-flex;
  flex: 0 0 14px;
  justify-content: center;
  margin-right: 12px;
  padding-top: 1px;
  font-size: 14px;
  line-height: 15px;
`;

const RowText = styled.span`
  display: block;
  flex: 1 1 auto;
  min-width: 0;
`;

const RowTitle = styled.span`
  display: block;
  overflow: hidden;
  color: ${orbitTokens.text};
  ${orbitType.containerTitle}
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RowSubtitle = styled.span`
  display: block;
  margin-top: 2px;
  color: ${orbitTokens.textMuted};
  ${orbitType.containerBody}
  font-weight: 400;
`;

const SessionSubtitle = styled(RowSubtitle)`
  ${orbitType.containerBody}
`;

const ViewAll = styled.button`
  display: block;
  width: 100%;
  margin: 12px 0 0;
  padding: 11px;
  background: transparent;
  border: 1px solid ${orbitTokens.border};
  border-radius: 10px;
  color: ${orbitTokens.textSecondary};
  cursor: pointer;
  font-family: inherit;
  ${orbitType.containerBody}
  font-weight: 700;
  text-align: center;

  /* figma-spec-v3.md section 4c lists 4952:9043 as hovering "as row": stroke and ink together. */
  &:hover,
  &:focus-visible {
    border-color: ${orbitTokens.action};
    color: ${orbitTokens.text};
    outline: none;
  }
`;

export interface IOrbitHomeStateProps {
  /** Opens the conversation list, the destination of `View all history` (`4952:9043`). */
  onViewHistory: () => void;
}

/**
 * Home panel `4952:8961`: greet card, the prompt starters as suggested-action rows, the recent
 * conversations, and the entry to the full history. Replaces the starter-chip empty state.
 */
const OrbitHomeState: React.FC<IOrbitHomeStateProps> = ({ onViewHistory }) => {
  const { t } = useMultiLanguageTranslation();
  const { ask, isAsking } = useOrbitContext();
  const { screen, cards, grants } = useOrbitStarters();
  const { sessions: chats, select } = useOrbitConversations(ORBIT_ALL_CHATS);

  const starters = useMemo(
    () =>
      [...ORBIT_GLOBAL_STARTERS, ...getContextualStarters(screen, cards)].filter(starter =>
        isStarterAllowed(starter, grants)
      ),
    [screen, cards, grants]
  );

  const sessions = useMemo(() => chats.slice(0, RECENT_SESSION_LIMIT), [chats]);

  const hasContext = getOrbitScreenLabelKey(screen) !== undefined;

  const handleStarterClick = useCallback(
    (starter: OrbitPromptStarter) => {
      ask(t(starter.labelKey));
    },
    [ask, t]
  );

  return (
    <Root data-testid="orbit-home-state">
      {hasContext ? null : (
        <GreetCard data-testid="orbit-home-greet">
          <GreetTitle>{t(`${TOrbit}.home.idleTitle`)}</GreetTitle>
          <GreetBody>{t(`${TOrbit}.home.idleBody`)}</GreetBody>
        </GreetCard>
      )}
      <ActionsLabel id="orbit-home-actions-heading">{t(`${TOrbit}.home.suggested`)}</ActionsLabel>
      <List aria-labelledby="orbit-home-actions-heading">
        {starters.map(starter => (
          <li key={starter.id}>
            <Row
              type="button"
              disabled={isAsking}
              data-testid={`orbit-home-action-${starter.id}`}
              onClick={() => handleStarterClick(starter)}
            >
              <IconTile aria-hidden="true">
                <StarterIcon id={starter.id} />
              </IconTile>
              <RowText>
                <RowTitle>{t(starter.labelKey)}</RowTitle>
                <RowSubtitle>{t(`${TOrbit}.starters.detail.${starter.id}`)}</RowSubtitle>
              </RowText>
            </Row>
          </li>
        ))}
      </List>
      {sessions.length > 0 ? (
        <>
          <SessionsLabel id="orbit-home-sessions-heading">
            {t(`${TOrbit}.home.recent`)}
          </SessionsLabel>
          <List aria-labelledby="orbit-home-sessions-heading">
            {sessions.map(session => {
              const when = orbitRelativeTime(session.last_activity_at);

              return (
                <li key={session.session_id}>
                  <Row
                    type="button"
                    data-testid="orbit-home-session"
                    data-session-id={session.session_id}
                    onClick={() => select(session)}
                  >
                    <SessionIcon aria-hidden="true">
                      <MessageOutlined data-testid="orbit-home-session-icon" />
                    </SessionIcon>
                    <RowText>
                      <RowTitle>{session.title}</RowTitle>
                      {when !== null ? <SessionSubtitle>{when}</SessionSubtitle> : null}
                    </RowText>
                  </Row>
                </li>
              );
            })}
          </List>
        </>
      ) : null}
      <ViewAll type="button" data-testid="orbit-view-history" onClick={onViewHistory}>
        {t(`${TOrbit}.home.viewAll`)}
      </ViewAll>
    </Root>
  );
};

export default OrbitHomeState;
